import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { GraduationCap, LogIn } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { joinClassByCode, listStudentClasses } from "@/lib/teacher.functions";

export const Route = createFileRoute("/_app/classes")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Le mie classi — Lumen" },
      {
        name: "description",
        content: "Entra nella classe del tuo professore con un codice e segui bacheca, materiali e attività.",
      },
      { property: "og:title", content: "Le mie classi — Lumen" },
      { property: "og:description", content: "Entra nella classe del tuo professore con un codice." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: StudentClasses,
});

type Klass = { id: string; name: string; subject: string; school_level: string | null };

function StudentClasses() {
  const { t } = useTranslation();
  const listFn = useServerFn(listStudentClasses);
  const joinFn = useServerFn(joinClassByCode);
  const [classes, setClasses] = useState<Klass[]>([]);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setClasses((await listFn({ data: undefined } as never)) as Klass[]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const join = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const res = await joinFn({ data: { code: code.trim() } });
      if (!res.ok) {
        toast.error(
          res.reason === "closed"
            ? t("teacher.join.closed")
            : res.reason === "own_class"
              ? t("teacher.join.ownClass")
              : t("teacher.join.notFound"),
        );
        return;
      }
      toast.success(t("teacher.join.joined", { name: res.name }));
      setCode("");
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="space-y-5 px-4 py-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">{t("teacher.join.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("teacher.join.subtitle")}</p>
      </header>

      <Card className="card-soft space-y-3 border-0 p-4">
        <Label htmlFor="join-code">{t("teacher.join.code")}</Label>
        <div className="flex flex-wrap gap-2">
          <Input
            id="join-code"
            value={code}
            className="min-w-0 flex-1 uppercase tracking-widest"
            maxLength={12}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
          <Button className="min-h-11" disabled={busy || !code.trim()} onClick={join}>
            <LogIn className="mr-1.5 h-4 w-4" aria-hidden />
            {t("teacher.join.submit")}
          </Button>
        </div>
      </Card>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">{t("teacher.join.myClasses")}</h2>
        {loading ? (
          <div className="h-20 animate-pulse rounded-3xl bg-muted/50" />
        ) : classes.length === 0 ? (
          <Card className="card-soft border-0 p-5 text-sm text-muted-foreground">{t("teacher.dash.empty")}</Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {classes.map((c) => (
              <Card key={c.id} className="card-soft space-y-2 border-0 p-4">
                <p className="break-words text-sm font-semibold leading-snug">{c.name}</p>
                <p className="break-words text-xs text-muted-foreground">{c.subject}</p>
                <Button asChild size="sm" variant="secondary" className="min-h-10 w-full">
                  <Link to="/class/$classId" params={{ classId: c.id }}>
                    {t("teacher.classes.open")}
                  </Link>
                </Button>
              </Card>
            ))}
          </div>
        )}
      </section>

      <Card className="card-soft flex flex-wrap items-center justify-between gap-2 border-0 p-4">
        <span className="min-w-0 text-sm text-muted-foreground">{t("teacher.role.teacherDesc")}</span>
        <Button asChild size="sm" variant="ghost" className="min-h-10">
          <Link to="/teacher">
            <GraduationCap className="mr-1.5 h-4 w-4" aria-hidden />
            {t("teacher.dash.title")}
          </Link>
        </Button>
      </Card>
    </main>
  );
}
